import { Link } from "react-router-dom";
import { Mail, MapPin } from "lucide-react";

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="bg-[#ffffff] text-[#000000]">
      <section className="relative h-[60vh] min-h-[420px] overflow-hidden border-b border-[#000000]/10">
        <img
          src="https://images.unsplash.com/photo-1511280303142-0051e93baeeb"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#000000]/40 to-[#000000]" />
        <div className="leather-grain absolute inset-0" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 h-full flex flex-col justify-end pb-14">
          <p className="overline-light">BOSCAGE Elegance</p>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] mt-4 text-white">
            Premium leather, <span className="italic text-[#c9a84c]">made personal.</span>
          </h1>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 md:px-10 py-24">
        <p className="overline" data-testid="our-story-overline">Our Story</p>
        <h2 className="font-serif text-3xl md:text-4xl leading-tight mt-3 text-[#000000]">
          From a leather market in Pakistan to <span className="italic text-[#c9a84c]">launching for the USA.</span>
        </h2>

        <div
          className="mt-10 space-y-6 text-base md:text-lg leading-relaxed text-[#000000]/85"
          data-testid="our-story-body"
        >
          <p>
            Boscage isn&rsquo;t just a brand for me; it&rsquo;s a journey that
            started with a simple passion for leather. Back in 2019, my partner
            Adeel and I decided to turn this passion into something real. We
            didn&rsquo;t have a big setup&mdash;we just went straight to the
            leather markets, spent hours picking out skins ourselves, and
            created our very first patterns. Those first few jackets were sold
            to our close circle of friends and family. The feedback we got was
            so honest and encouraging that it pushed us to do more. We started
            exporting bulk finished leather, and that experience taught us so
            much about the industry.
          </p>
          <p>
            We chose leather because it&rsquo;s a product of nature. It&rsquo;s
            complex, raw, and has a luxury feel that is loved all over the
            world. We were always inspired by the journeys of brands like
            Bodaskins and Represent, and we wanted to bring that same premium
            quality to the market. During this time, my close friend Basit, who
            has been a huge support and is a shareholder in the brand, gave us
            the name &ldquo;Boscage.&rdquo; It felt perfect for what we were
            trying to build.
          </p>
          <p>
            But the journey wasn&rsquo;t easy. In 2020, we took a big leap and
            launched on Amazon. We were full of hope, but we faced a lot of
            challenges that we weren&rsquo;t prepared for. We suffered huge
            financial losses, and eventually, we went bankrupt. By 2021, we
            were out of Amazon and pretty much back to zero. It was a tough
            time, but Adeel and I didn&rsquo;t want to give up. For the next
            two years, we worked hard to build our capital again. We spent
            that time fixing our mistakes, changing manufacturers, and
            perfecting our leather selection. We made sure that even in our
            darkest time, our commitment to quality never changed.
          </p>
          <p>
            In 2023, with a little bit of saved capital and a lot more
            experience, we stepped back into the leather market. This time, we
            had a proper strategy. We started selling locally and the response
            was amazing. People recognized the effort we put into every stitch.
            Now, in 2026, we are finally launching again in the online market,
            specifically for the USA. We are bringing the same premium quality
            leather and the same dedication to service that we started with,
            but this time, we are stronger. Boscage is more than a jacket;
            it&rsquo;s a story of not giving up, and we are proud to finally
            share it with the world.
          </p>
        </div>

        {/* Timeline strip */}
        <div
          className="mt-20 border-t border-[#000000]/10 pt-10"
          data-testid="our-story-timeline"
        >
          <p className="overline">A short timeline</p>
          <ol className="grid sm:grid-cols-4 gap-6 mt-6">
            {[
              ["2019", "Two friends, one leather market."],
              ["2020", "Amazon launch, hard lessons."],
              ["2023", "Back in the market with a real plan."],
              ["2026", "Boscage online, for the USA."],
            ].map(([year, line]) => (
              <li key={year} className="border-l border-[#c9a84c] pl-4">
                <p className="font-serif text-3xl text-[#000000]">{year}</p>
                <p className="text-sm text-[#000000]/65 mt-2 leading-relaxed">
                  {line}
                </p>
              </li>
            ))}
          </ol>
        </div>

        <div className="grid md:grid-cols-2 gap-12 mt-20 border-t border-[#000000]/10 pt-10">
          <Block
            title="Master craftsmanship"
            text="Each jacket is hand-cut and hand-finished by master craftspeople, then quality-checked at our USA inspection facility before it ships to you."
          />
          <Block
            title="Heritage hides"
            text="We source from established tanneries that use slow, traditional vegetable-tanning techniques — giving our leather the depth that years of wear only deepen."
          />
          <Block
            title="Numbered & registered"
            text="Each jacket is numbered and registered to its first owner through your BOSCAGE VIP account, eligible for our Renewal Program for the next three years."
          />
          <Block
            title="Built to outlive"
            text="BOSCAGE pieces are quality-built for a lifetime. We stand behind every order with our 14-day fit guarantee and lifetime care advice."
          />
        </div>

        <div className="mt-20 border-t border-[#000000]/10 pt-10">
          <p className="overline">Atelier values</p>
          <ul className="grid sm:grid-cols-3 gap-6 mt-6">
            {[
              ["Slowness", "We do not chase seasons. Patterns evolve only when the work demands."],
              ["Repair", "We accept any BOSCAGE piece, within program windows, for repair and renewal."],
              ["Names", "Every owner becomes part of the BOSCAGE register — for life."],
            ].map(([h, t]) => (
              <li key={h}>
                <h4 className="font-serif text-xl">{h}</h4>
                <p className="text-sm text-[#000000]/65 mt-2 leading-relaxed">{t}</p>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact card */}
        <div
          className="mt-20 grid md:grid-cols-2 gap-8 bg-[#f5f5f5] p-8 md:p-10"
          data-testid="about-contact-card"
        >
          <div>
            <p className="overline text-[#000000]">Visit / Reach us</p>
            <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">
              Headquarters
            </h2>
            <p className="mt-4 text-[#000000]/70 leading-relaxed max-w-md">
              For inquiries, custom orders, trade-in valuations, or partnership
              opportunities, our team is here to help.
            </p>
          </div>

          <dl className="space-y-6 text-sm">
            <div data-testid="about-address">
              <dt className="overline flex items-center gap-2">
                <MapPin size={14} strokeWidth={1.5} className="text-[#000000]" />
                Address
              </dt>
              <dd className="mt-1 not-italic text-[#000000]/85 leading-relaxed">
                BOSCAGE Elegance
                <br />
                5856 Mount Vernon Dr, VA,
                <br />
                Alexandria, VA 22303
                <br />
                United States
              </dd>
            </div>
            <div data-testid="about-email">
              <dt className="overline flex items-center gap-2">
                <Mail size={14} strokeWidth={1.5} className="text-[#000000]" />
                Email
              </dt>
              <dd className="mt-1">
                <a
                  href="mailto:hello@boscage.com"
                  className="text-[#000000] hover:text-[#000000] transition-colors"
                >
                  hello@boscage.com
                </a>
              </dd>
            </div>
            <Link
              to="/contact"
              className="btn-vip inline-flex w-auto"
              data-testid="about-contact-cta"
            >
              <span>Write to us</span>
            </Link>
          </dl>
        </div>
      </section>
    </div>
  );
}

const Block = ({ title, text }) => (
  <div>
    <h3 className="font-serif text-2xl text-[#000000]">{title}</h3>
    <p className="mt-3 text-[#000000]/75 leading-relaxed">{text}</p>
  </div>
);
